import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiPatch, apiPost, fetchAllPaginated } from '../services/api';
import { getNavSectionsDoPerfil } from '../config/navigationConfig';
import useEffectiveCity from '../hooks/useEffectiveCity';
import SearchableSelect from './SearchableSelect';

/**
 * Configurações → Acessos por Usuário: o administrador ajusta cada conta —
 * liga ou desliga criar, editar e excluir, e esconde abas do menu lateral.
 * As três chaves desligadas deixam a conta em "somente visualizar".
 *
 * O ajuste só RESTRINGE o que o perfil permite. O que o perfil não dá
 * aparece desligado e travado, para não sugerir que marcar liberaria. A
 * trava de criar/editar/excluir vale no servidor inteiro; as abas saem do
 * menu e os endereços delas deixam de abrir.
 */

// O que cada perfil já não faz, independente do ajuste
// (api/permissions.py). Gestor e Secretário só leem; a Referência Técnica
// não exclui; o Supervisor edita e exclui nas escolas dele, mas não
// cadastra. O Diretor(a) matricula aluno (Criar) e muda a situação dele
// (Editar) — e nada mais: não exclui (api/authentication.py).
const LIMITES_DO_PERFIL = {
  gestor: { criar: false, editar: false, excluir: false },
  secretario: { criar: false, editar: false, excluir: false },
  referencia_tecnica: { criar: true, editar: true, excluir: false },
  supervisor: { criar: false, editar: true, excluir: true },
  diretor: { criar: true, editar: true, excluir: false },
};
const limitesDo = (role) => LIMITES_DO_PERFIL[role] || { criar: true, editar: true, excluir: true };

const CHAVES = [
  { campo: 'pode_criar', limite: 'criar', rotulo: 'Criar', apoio: 'Desligado, a conta não cadastra nem lança registros novos.', semPerfil: 'O perfil não cria registros.' },
  { campo: 'pode_editar', limite: 'editar', rotulo: 'Editar', apoio: 'Desligado, a conta não altera o que já existe.', semPerfil: 'O perfil não edita.' },
  { campo: 'pode_excluir', limite: 'excluir', rotulo: 'Excluir', apoio: 'Desligado, a conta não apaga registros.', semPerfil: 'O perfil não exclui.' },
];

const POR_PAGINA = 50;

const ajusteDe = (u) => ({
  pode_criar: u.pode_criar !== false,
  pode_editar: u.pode_editar !== false,
  pode_excluir: u.pode_excluir !== false,
  abas_ocultas: Array.isArray(u.abas_ocultas) ? u.abas_ocultas : [],
});

const mesmoAjuste = (a, b) => a.pode_criar === b.pode_criar
  && a.pode_editar === b.pode_editar
  && a.pode_excluir === b.pode_excluir
  && a.abas_ocultas.length === b.abas_ocultas.length
  && a.abas_ocultas.every((h) => b.abas_ocultas.includes(h));

const personalizado = (u) => !mesmoAjuste(ajusteDe(u), {
  pode_criar: true, pode_editar: true, pode_excluir: true, abas_ocultas: [],
});

// Nada do que o perfil permite escrever ficou ligado: a conta só olha.
const somenteVisualizar = (ajuste, limites) => CHAVES.every((c) => !limites[c.limite] || !ajuste[c.campo]);

const nomeDe = (u) => [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.username;

function Chave({ ligada, onChange, disabled, rotulo, apoio, darkMode }) {
  return (
    <label className={`flex items-center gap-2 select-none ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`} title={apoio}>
      <button
        type="button"
        role="switch"
        aria-checked={ligada}
        disabled={disabled}
        onClick={() => onChange(!ligada)}
        className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors ${
          ligada ? 'bg-[#13983D]' : darkMode ? 'bg-slate-600' : 'bg-slate-300'
        }`}
      >
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${ligada ? 'translate-x-4' : 'translate-x-0.5'}`} />
      </button>
      <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{rotulo}</span>
    </label>
  );
}

const classeBotaoPequeno = (darkMode) => `px-3 py-1.5 rounded-lg text-xs font-medium border whitespace-nowrap ${
  darkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
}`;

// Abas que o perfil enxerga e que podem ser escondidas (o Dashboard não).
const ocultaveisDo = (role) => getNavSectionsDoPerfil(role).flatMap((s) => s.items.filter((i) => !i.fixo));

const contarOcultas = (role, ocultas) => ocultaveisDo(role).filter((i) => ocultas.includes(i.href)).length;

/** Checkboxes das abas do menu de um perfil; marcada = aparece no menu. */
function SeletorDeAbas({ role, perfilRotulo, ocultas, onChange, darkMode, mutedClass }) {
  const secoes = getNavSectionsDoPerfil(role);
  const botaoPequeno = classeBotaoPequeno(darkMode);
  const forte = darkMode ? 'text-slate-200' : 'text-slate-700';
  const alternar = (href) => onChange(ocultas.includes(href) ? ocultas.filter((h) => h !== href) : [...ocultas, href]);

  return (
    <div className={`mt-3 p-4 rounded-lg ${darkMode ? 'bg-slate-900/40' : 'bg-slate-50'}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className={`text-xs ${mutedClass}`}>
          Marcadas aparecem no menu. Só estão aqui as abas que o perfil {perfilRotulo} enxerga.
        </p>
        <div className="flex gap-2">
          <button type="button" className={botaoPequeno} onClick={() => onChange([])}>
            Mostrar todas
          </button>
          <button type="button" className={botaoPequeno} onClick={() => onChange(ocultaveisDo(role).map((i) => i.href))}>
            Ocultar todas
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {secoes.map((secao) => (
          <div key={secao.title}>
            <p className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${mutedClass}`}>{secao.title}</p>
            {secao.items.map((item) => (
              <label
                key={item.href}
                className={`flex items-center gap-2 py-1 text-sm ${forte} ${item.fixo ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                title={item.fixo ? 'Sempre visível: é a tela inicial do sistema.' : item.description}
              >
                <input
                  type="checkbox"
                  checked={item.fixo || !ocultas.includes(item.href)}
                  disabled={item.fixo}
                  onChange={() => alternar(item.href)}
                  className="h-4 w-4 accent-[#0974BA]"
                />
                <span aria-hidden="true">{item.icon}</span>
                <span>{item.label}</span>
              </label>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * "Aplicar a todos": com um perfil escolhido no filtro, monta um ajuste e
 * grava o mesmo em todas as contas listadas, de uma vez. Substitui o ajuste
 * que cada uma tinha — por isso pede confirmação.
 */
function AplicarATodos({ role, perfilRotulo, alvos, onAplicado, darkMode, mutedClass }) {
  const [ajuste, setAjuste] = useState({ pode_criar: true, pode_editar: true, pode_excluir: true, abas_ocultas: [] });
  const [abasAbertas, setAbasAbertas] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [msg, setMsg] = useState(null);
  const limites = limitesDo(role);
  const botaoPequeno = classeBotaoPequeno(darkMode);
  const qtdOcultas = contarOcultas(role, ajuste.abas_ocultas);
  const n = alvos.length;

  const mudar = (mudanca) => { setMsg(null); setAjuste((a) => ({ ...a, ...mudanca })); };

  const aplicar = async () => {
    const quem = `${n} ${n === 1 ? 'usuário' : 'usuários'} (${perfilRotulo})`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Aplicar este ajuste a ${quem}?\n\nIsso substitui o ajuste atual de cada um.`)) return;
    setAplicando(true);
    setMsg(null);
    try {
      const r = await apiPost('/users/permissoes-em-lote/', { ids: alvos.map((u) => u.id), ...ajuste });
      onAplicado(r.usuarios || []);
      setMsg({ ok: true, texto: `Aplicado a ${r.atualizados} ${r.atualizados === 1 ? 'usuário' : 'usuários'}.` });
    } catch (err) {
      setMsg({ ok: false, texto: err?.message || 'Não foi possível aplicar.' });
    } finally {
      setAplicando(false);
    }
  };

  return (
    <div className={`mb-4 p-4 rounded-lg border ${darkMode ? 'border-[#0974BA]/40 bg-[#0974BA]/10' : 'border-[#0974BA]/20 bg-[#0974BA]/5'}`}>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
        <div className="flex-1 basis-56 min-w-0">
          <p className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>Aplicar a todos — {perfilRotulo}</p>
          <p className={`text-xs ${mutedClass}`}>
            Vale para os {n} {n === 1 ? 'usuário listado' : 'usuários listados'} abaixo (com a busca e os filtros atuais).
          </p>
        </div>
        {CHAVES.map((c) => (
          <Chave
            key={c.campo}
            ligada={limites[c.limite] && ajuste[c.campo]}
            disabled={!limites[c.limite]}
            onChange={(v) => mudar({ [c.campo]: v })}
            rotulo={c.rotulo}
            apoio={limites[c.limite] ? c.apoio : c.semPerfil}
            darkMode={darkMode}
          />
        ))}
        <button type="button" onClick={() => setAbasAbertas((v) => !v)} className={botaoPequeno} aria-expanded={abasAbertas}>
          Abas do menu{qtdOcultas ? ` · ${qtdOcultas} oculta${qtdOcultas > 1 ? 's' : ''}` : ''} {abasAbertas ? '▴' : '▾'}
        </button>
        <button
          type="button"
          onClick={aplicar}
          disabled={aplicando || n === 0}
          className="px-4 py-1.5 rounded-lg text-xs font-medium bg-[#0974BA] hover:bg-[#0974BA]/80 text-white disabled:opacity-60 whitespace-nowrap"
        >
          {aplicando ? 'Aplicando...' : `Aplicar a todos (${n})`}
        </button>
      </div>
      {msg && <p className={`mt-2 text-xs ${msg.ok ? 'text-[#13983D]' : 'text-[#E83A3C]'}`}>{msg.texto}</p>}
      {abasAbertas && (
        <SeletorDeAbas
          role={role}
          perfilRotulo={perfilRotulo}
          ocultas={ajuste.abas_ocultas}
          onChange={(abas) => mudar({ abas_ocultas: abas })}
          darkMode={darkMode}
          mutedClass={mutedClass}
        />
      )}
    </div>
  );
}

function AcessosPorUsuario({ darkMode, cardClass, titleClass, mutedClass }) {
  const cidade = useEffectiveCity();
  const [usuarios, setUsuarios] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [busca, setBusca] = useState('');
  const [filtroPerfil, setFiltroPerfil] = useState('');
  const [soPersonalizados, setSoPersonalizados] = useState(false);
  const [limite, setLimite] = useState(POR_PAGINA);
  const [rascunhos, setRascunhos] = useState({}); // id → ajuste em edição
  const [abasAbertas, setAbasAbertas] = useState(null); // id do usuário
  const [salvando, setSalvando] = useState(null);
  const [mensagens, setMensagens] = useState({}); // id → { ok, texto }

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const lista = await fetchAllPaginated('/users/', cidade?.id ? { city: cidade.id } : {});
      setUsuarios(lista || []);
    } catch (err) {
      setErro(err?.message || 'Não foi possível carregar os usuários.');
    } finally {
      setCarregando(false);
    }
  }, [cidade?.id]);

  useEffect(() => { carregar(); }, [carregar]);

  const perfis = useMemo(() => {
    const vistos = new Map();
    usuarios.forEach((u) => { if (!vistos.has(u.role)) vistos.set(u.role, u.role_display || u.role); });
    return [...vistos].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [usuarios]);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return usuarios
      .filter((u) => !filtroPerfil || u.role === filtroPerfil)
      .filter((u) => !soPersonalizados || personalizado(u))
      .filter((u) => !q || [nomeDe(u), u.username, u.email, u.city_name]
        .some((v) => (v || '').toLowerCase().includes(q)))
      .sort((a, b) => nomeDe(a).localeCompare(nomeDe(b)));
  }, [usuarios, busca, filtroPerfil, soPersonalizados]);

  useEffect(() => { setLimite(POR_PAGINA); }, [busca, filtroPerfil, soPersonalizados]);

  const ajusteAtual = (u) => rascunhos[u.id] || ajusteDe(u);

  const alterar = (u, mudanca) => {
    setMensagens((m) => ({ ...m, [u.id]: null }));
    setRascunhos((r) => {
      const proximo = { ...(r[u.id] || ajusteDe(u)), ...mudanca };
      const resto = { ...r };
      if (mesmoAjuste(proximo, ajusteDe(u))) delete resto[u.id];
      else resto[u.id] = proximo;
      return resto;
    });
  };

  // Depois do "aplicar a todos": a lista passa a mostrar o que foi gravado,
  // e os rascunhos dessas contas deixam de valer.
  const aplicadoEmLote = (atualizados) => {
    const porId = new Map(atualizados.map((x) => [x.id, x]));
    setUsuarios((lista) => lista.map((x) => (porId.has(x.id) ? { ...x, ...porId.get(x.id) } : x)));
    setRascunhos((r) => {
      const resto = { ...r };
      porId.forEach((_, id) => { delete resto[id]; });
      return resto;
    });
    setMensagens({});
  };

  const descartar = (u) => {
    setRascunhos((r) => { const resto = { ...r }; delete resto[u.id]; return resto; });
    setMensagens((m) => ({ ...m, [u.id]: null }));
  };

  const salvar = async (u) => {
    const ajuste = rascunhos[u.id];
    if (!ajuste) return;
    setSalvando(u.id);
    try {
      const atualizado = await apiPatch(`/users/${u.id}/permissoes/`, ajuste);
      setUsuarios((lista) => lista.map((x) => (x.id === u.id ? { ...x, ...atualizado } : x)));
      descartar(u);
      setMensagens((m) => ({
        ...m,
        [u.id]: { ok: true, texto: 'Salvo. Vale na próxima vez que a pessoa abrir o sistema.' },
      }));
    } catch (err) {
      setMensagens((m) => ({ ...m, [u.id]: { ok: false, texto: err?.message || 'Não foi possível salvar.' } }));
    } finally {
      setSalvando(null);
    }
  };

  const campo = `w-full px-4 py-2.5 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${
    darkMode
      ? 'bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-500'
      : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'
  }`;
  const botaoPequeno = classeBotaoPequeno(darkMode);
  const linha = darkMode ? 'border-slate-700' : 'border-slate-100';
  const forte = darkMode ? 'text-slate-200' : 'text-slate-700';

  return (
    <div className={cardClass}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
        <div>
          <h2 className={`${titleClass} !mb-1`}>Acessos por Usuário</h2>
          <p className={`text-sm ${mutedClass} max-w-2xl`}>
            Ajuste o que cada pessoa pode fazer. Com Criar, Editar e Excluir desligados, a conta fica
            em somente visualizar. Escolha um perfil no filtro para aplicar o mesmo ajuste a todos de
            uma vez. O ajuste só restringe o que o perfil já permite — para dar mais acesso, troque o
            perfil em Usuários. Administradores têm acesso total.
          </p>
        </div>
        <button type="button" onClick={carregar} className={botaoPequeno}>Atualizar</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_220px_auto] gap-3 my-4 items-center">
        <input
          type="search"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar por nome, usuário, e-mail ou cidade..."
          className={campo}
        />
        <SearchableSelect
          value={filtroPerfil}
          onChange={setFiltroPerfil}
          options={perfis}
          emptyOptionLabel="Todos os perfis"
          darkMode={darkMode}
        />
        <Chave
          ligada={soPersonalizados}
          onChange={setSoPersonalizados}
          rotulo="Só personalizados"
          darkMode={darkMode}
        />
      </div>

      {erro && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${darkMode ? 'bg-[#E83A3C]/20 text-[#E83A3C]' : 'bg-[#E83A3C]/5 text-[#E83A3C]'}`}>
          {erro}
        </div>
      )}

      {!carregando && filtroPerfil && filtroPerfil !== 'administrador' && (
        <AplicarATodos
          key={filtroPerfil}
          role={filtroPerfil}
          perfilRotulo={perfis.find((p) => p.value === filtroPerfil)?.label || filtroPerfil}
          alvos={filtrados}
          onAplicado={aplicadoEmLote}
          darkMode={darkMode}
          mutedClass={mutedClass}
        />
      )}

      {carregando ? (
        <p className={`py-8 text-center ${mutedClass}`}>Carregando usuários...</p>
      ) : filtrados.length === 0 ? (
        <p className={`py-8 text-center ${mutedClass}`}>Nenhum usuário encontrado.</p>
      ) : (
        <>
          <p className={`text-xs mb-2 ${mutedClass}`}>
            {filtrados.length} {filtrados.length === 1 ? 'usuário' : 'usuários'}
            {cidade?.name ? ` · ${cidade.name}` : ''}
          </p>
          <ul>
            {filtrados.slice(0, limite).map((u) => {
              const ehAdmin = u.role === 'administrador';
              const ajuste = ajusteAtual(u);
              const limites = limitesDo(u.role);
              const sujo = !!rascunhos[u.id];
              const qtdOcultas = ehAdmin ? 0 : contarOcultas(u.role, ajuste.abas_ocultas);
              const msg = mensagens[u.id];

              return (
                <li key={u.id} className={`py-3 border-b last:border-0 ${linha}`}>
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1 basis-64">
                      <div className={`w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold ${
                        darkMode ? 'bg-slate-700 text-sideb-green' : 'bg-[#0974BA]/10 text-[#0974BA]'
                      }`}>
                        {nomeDe(u).split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className={`text-sm font-medium truncate ${forte}`}>
                          {nomeDe(u)}
                          {!u.is_active && <span className={`ml-2 text-xs font-normal ${mutedClass}`}>(inativo)</span>}
                        </p>
                        <p className={`text-xs truncate ${mutedClass}`}>
                          @{u.username} · {u.role_display || u.role}{u.city_name ? ` · ${u.city_name}` : ''}
                        </p>
                      </div>
                      {!ehAdmin && somenteVisualizar(ajuste, limites) && (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#0974BA]/15 text-[#0974BA] whitespace-nowrap">
                          Somente visualizar
                        </span>
                      )}
                      {!ehAdmin && personalizado(u) && (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#F9B047]/20 text-[#B07700] whitespace-nowrap">
                          Personalizado
                        </span>
                      )}
                    </div>

                    {ehAdmin ? (
                      <span className={`text-xs ${mutedClass}`}>Acesso total — não se aplica</span>
                    ) : (
                      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                        {CHAVES.map((c) => (
                          <Chave
                            key={c.campo}
                            ligada={limites[c.limite] && ajuste[c.campo]}
                            disabled={!limites[c.limite]}
                            onChange={(v) => alterar(u, { [c.campo]: v })}
                            rotulo={c.rotulo}
                            apoio={limites[c.limite] ? c.apoio : c.semPerfil}
                            darkMode={darkMode}
                          />
                        ))}
                        <button
                          type="button"
                          onClick={() => setAbasAbertas((id) => (id === u.id ? null : u.id))}
                          className={botaoPequeno}
                          aria-expanded={abasAbertas === u.id}
                        >
                          Abas do menu{qtdOcultas ? ` · ${qtdOcultas} oculta${qtdOcultas > 1 ? 's' : ''}` : ''} {abasAbertas === u.id ? '▴' : '▾'}
                        </button>
                        {sujo && (
                          <>
                            <button
                              type="button"
                              onClick={() => salvar(u)}
                              disabled={salvando === u.id}
                              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[#0974BA] hover:bg-[#0974BA]/80 text-white disabled:opacity-60"
                            >
                              {salvando === u.id ? 'Salvando...' : 'Salvar'}
                            </button>
                            <button type="button" onClick={() => descartar(u)} className={botaoPequeno}>Desfazer</button>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {msg && (
                    <p className={`mt-2 text-xs ${msg.ok ? 'text-[#13983D]' : 'text-[#E83A3C]'}`}>{msg.texto}</p>
                  )}

                  {!ehAdmin && abasAbertas === u.id && (
                    <SeletorDeAbas
                      role={u.role}
                      perfilRotulo={u.role_display || u.role}
                      ocultas={ajuste.abas_ocultas}
                      onChange={(abas) => alterar(u, { abas_ocultas: abas })}
                      darkMode={darkMode}
                      mutedClass={mutedClass}
                    />
                  )}
                </li>
              );
            })}
          </ul>
          {filtrados.length > limite && (
            <div className="mt-4 text-center">
              <button type="button" className={botaoPequeno} onClick={() => setLimite((l) => l + POR_PAGINA)}>
                Mostrar mais ({filtrados.length - limite} restantes)
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AcessosPorUsuario;
